import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useGoals, useProfiles } from "../goals/api";
import { PokeButton } from "../nudges/PokeButton";
import { MonthCalendar } from "./MonthCalendar";
import { useUploadAvatar } from "./avatar";
import { ProfileAvatar } from "../../components/ProfileAvatar";
import { useToast } from "../../components/Toast";
import { errorMessage } from "../../lib/errors";
import { EmptyState, Skeleton } from "../../components/ui";

/**
 * Profile tab, handoff 6a: gradient-ring avatar, name, a month calendar and the
 * goal button. Geist throughout; the page background is the handoff's subtle grey.
 */
const PAGE =
  "-mx-5 flex min-h-[calc(100dvh-76px-env(safe-area-inset-bottom))] flex-col gap-6 px-4 pt-safe text-[#0a0a0b]";

export function ProfilePage() {
  const { personId } = useParams<{ personId: string }>();
  const { userId } = useAuth();

  const profiles = useProfiles();
  const toast = useToast();
  const upload = useUploadAvatar(userId ?? "");
  const goals = useGoals();

  const person = profiles.data?.find((profile) => profile.id === personId);
  const isMe = personId === userId;
  const theirGoals =
    goals.data?.filter((goal) => goal.owner_id === personId) ?? [];

  if (profiles.isLoading) {
    return (
      <div className={PAGE}>
        <Skeleton className="mx-auto mt-8 size-[88px] !rounded-full" />
        <Skeleton className="mt-auto h-96" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="pt-safe">
        <EmptyState
          title="No such person."
          action={
            <Link to="/" className="btn btn-secondary no-underline">
              back to the feed
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className={PAGE}>
      <header className="flex flex-col items-center gap-3 pt-8">
        {isMe ? (
          <label className="relative cursor-pointer" aria-label="Change profile photo">
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={upload.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                upload.mutate(
                  { file, previousPath: person.avatar_path },
                  {
                    onError: (error) =>
                      toast(errorMessage(error, "Couldn't update your photo"), "error"),
                  },
                );
              }}
            />
            <ProfileAvatar
              name={person.display_name}
              avatarPath={person.avatar_path}
              size={88}
              ring={4}
              className={upload.isPending ? "opacity-50" : undefined}
            />
          </label>
        ) : (
          <ProfileAvatar
            name={person.display_name}
            avatarPath={person.avatar_path}
            size={88}
            ring={4}
          />
        )}
        <h1 className="font-[inherit] text-[26px] font-semibold leading-none tracking-[-0.03em]">
          {person.display_name}
        </h1>
      </header>

      <MonthCalendar
        personId={person.id}
        goals={theirGoals}
        joinedAt={person.created_at}
      />

      <div className="mb-2 mt-auto flex flex-col items-center gap-3">
        {isMe ? (
          <>
            <Link
              to="/goals/new"
              className="flex h-12 w-3/5 items-center justify-center rounded-full bg-[#0a0a0b] text-[15px] font-medium text-white no-underline transition-colors duration-150 hover:bg-[#1f1f23] active:bg-[#08080a]"
            >
              Set a New Goal
            </Link>
            {/* The handoff drops Settings from this screen without saying where
                it goes, so it stays reachable as a quiet link. */}
            <Link
              to="/settings"
              className="text-xs text-[#8a8a94] no-underline"
            >
              Settings
            </Link>
          </>
        ) : (
          <PokeButton targetId={person.id} targetName={person.display_name} />
        )}
      </div>
    </div>
  );
}
